import type { Stroke } from '../types/app.js';
import { getRenderedMarkerLabelOffset, getRenderedTextFontSize, getRenderedWidth, isMarkerLabelStroke } from './map-math.js';
import { getMarkerScreenBounds } from './marker-renderer.js';
import { renderStroke } from './stroke-renderer.js';
import { getStrokeOpacity } from './stroke-style.js';

function wrapMarkerLabel(text: string): string {
  const context = document.createElement('canvas').getContext('2d');
  if (!context) return text;
  context.font = '600 13px system-ui, sans-serif';
  const lines: string[] = [];
  for (const paragraph of text.split('\n')) {
    let line = '';
    for (const word of paragraph.split(/\s+/)) {
      if (context.measureText(`${line} ${word}`).width <= 240) {
        line = line ? `${line} ${word}` : word;
        continue;
      }
      if (line) lines.push(line);
      line = '';
      for (const character of word) {
        if (line && context.measureText(line + character).width > 240) {
          lines.push(line);
          line = '';
        }
        line += character;
      }
    }
    lines.push(line);
  }
  return lines.join('\n');
}

export function getGlobeTextLayout(stroke: Stroke, textWidth: number, currentZoom = stroke.zoom) {
  const fontSize = getRenderedTextFontSize(stroke, currentZoom);
  const textHeight = (stroke.text || '').split('\n').length * fontSize * 1.2;
  const rotation = stroke.rotation || 0;
  const padding = Math.max(2, fontSize * 0.3);
  const width = Math.ceil(Math.abs(Math.cos(rotation)) * textWidth + Math.abs(Math.sin(rotation)) * textHeight + padding * 2);
  const height = Math.ceil(Math.abs(Math.sin(rotation)) * textWidth + Math.abs(Math.cos(rotation)) * textHeight + padding * 2);
  return {
    width,
    height,
    centerX: isMarkerLabelStroke(stroke) ? 0 : textWidth / 2,
    centerY: getRenderedMarkerLabelOffset(stroke, currentZoom) + textHeight / 2,
  };
}

export function createGlobeTextBillboard(
  stroke: Stroke,
  alpha = 1,
  currentZoom = stroke.zoom,
) {
  if (!stroke.text || !stroke.points.length) return undefined;
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) return undefined;
  context.font = `bold ${getRenderedTextFontSize(stroke, currentZoom)}px system-ui, sans-serif`;
  const textWidth = Math.max(...stroke.text.split('\n').map((line) => context.measureText(line).width));
  const layout = getGlobeTextLayout(stroke, textWidth, currentZoom);
  const pixelRatio = Math.min(window.devicePixelRatio || 1, 2, 4096 / Math.max(layout.width, layout.height));
  canvas.width = Math.max(1, Math.ceil(layout.width * pixelRatio));
  canvas.height = Math.max(1, Math.ceil(layout.height * pixelRatio));
  context.scale(pixelRatio, pixelRatio);
  renderStroke(context, stroke, {
    project: () => ({ x: layout.width / 2 - layout.centerX, y: layout.height / 2 - layout.centerY }),
    currentZoom,
    applyStrokeOpacity: false,
  });
  return { image: canvas, pixelRatio, offset: [layout.centerX, layout.centerY], opacity: alpha * getStrokeOpacity(stroke) };
}

export function createGlobeMarkerLabel(
  stroke: Stroke,
  alpha = 1,
  currentZoom = stroke.zoom,
) {
  if (!stroke.text?.trim()) return undefined;
  const bounds = getMarkerScreenBounds({ x: 0, y: 0 }, getRenderedWidth(stroke, currentZoom), stroke.markerIcon);
  return createGlobeLabelCanvas(wrapMarkerLabel(stroke.text), { offset: [0, bounds.maxY + 15], color: '#0f172a', background: '#ffffff', alpha: alpha * getStrokeOpacity(stroke) });
}

export function createGlobeLabelCanvas(text: string, options: { offset?: number[]; color?: string; background?: string; alpha?: number } = {}) {
  const image = document.createElement('canvas');
  const context = image.getContext('2d')!;
  const font = '600 13px system-ui, sans-serif';
  context.font = font;
  const lines = text.split('\n');
  const width = Math.ceil(Math.max(...lines.map(line => context.measureText(line).width)) + 20);
  const height = lines.length * 17 + 14;
  image.width = width * 2;
  image.height = height * 2;
  context.scale(2, 2);
  context.fillStyle = options.background ?? '#0f172a';
  context.fillRect(0, 0, width, height);
  context.font = font;
  context.fillStyle = options.color ?? '#ffffff';
  context.textAlign = 'center';
  context.textBaseline = 'top';
  lines.forEach((line, index) => context.fillText(line, width / 2, 7 + index * 17));
  return { image, pixelRatio: 2, offset: options.offset ?? [0, -20], opacity: options.alpha ?? 1 };
}
